import React from 'react';
import { Lightbulb, AlertTriangle, Info } from 'lucide-react';
import type { LearnAction, LearnBlockGroup, LearnBlockItem } from '@/client/types/learn';

interface LearnBlockPreviewProps {
  title?: string;
  summary?: string;
  minutes?: number;
  topicTitle?: string;
  blocks?: LearnBlockGroup[];
  actions?: LearnAction[];
  className?: string;
}

const CALLOUT_STYLES = {
  tip: {
    container: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    icon: Lightbulb,
    iconColor: 'text-emerald-600',
    label: 'Tip',
  },
  warning: {
    container: 'bg-amber-50 border-amber-200 text-amber-900',
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    label: 'Warning',
  },
  note: {
    container: 'bg-blue-50 border-blue-200 text-blue-900',
    icon: Info,
    iconColor: 'text-blue-600',
    label: 'Note',
  },
};

export const LearnBlockPreview: React.FC<LearnBlockPreviewProps> = ({
  title,
  summary,
  minutes,
  topicTitle,
  blocks = [],
  actions = [],
  className = '',
}) => {
  const renderItem = (item: LearnBlockItem, idx: number) => {
    switch (item.type) {
      case 'p':
        return (
          <p key={idx} className="text-sm leading-relaxed text-gray-700 whitespace-pre-line my-2">
            {item.text}
          </p>
        );
      case 'ul':
        return (
          <ul key={idx} className="list-disc list-inside space-y-1 text-sm text-gray-700 my-2 pl-2">
            {item.items.map((it, i) => (
              <li key={i}>{it}</li>
            ))}
          </ul>
        );
      case 'ol':
        return (
          <ol key={idx} className="list-decimal list-inside space-y-1 text-sm text-gray-700 my-2 pl-2">
            {item.items.map((it, i) => (
              <li key={i}>{it}</li>
            ))}
          </ol>
        );
      case 'callout': {
        const style = CALLOUT_STYLES[item.tone] || CALLOUT_STYLES.note;
        const Icon = style.icon;
        return (
          <div
            key={idx}
            className={`my-3 rounded-lg border p-3 flex gap-2.5 items-start text-xs leading-normal ${style.container}`}
          >
            <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${style.iconColor}`} />
            <div className="flex-1">
              <span className="font-semibold uppercase tracking-wider text-[10px] block mb-0.5 opacity-80">
                {style.label}
              </span>
              <p className="whitespace-pre-line">{item.text}</p>
            </div>
          </div>
        );
      }
      case 'image':
        return (
          <figure key={idx} className="my-3 space-y-1.5">
            <img src={item.src} alt={item.alt} loading="lazy" className="w-full rounded-lg border border-gray-200" />
            {item.caption && <figcaption className="text-xs text-gray-500">{item.caption}</figcaption>}
          </figure>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 shadow-sm ${className}`}>
      {topicTitle && (
        <span className="inline-block text-xs font-semibold uppercase tracking-wider text-blue-600 mb-1">
          {topicTitle}
        </span>
      )}
      {title && <h1 className="text-xl font-bold text-gray-900 mb-2">{title}</h1>}

      <div className="flex items-center gap-3 text-xs text-gray-500 mb-4 pb-3 border-b border-gray-100">
        {minutes != null && minutes > 0 && <span>{minutes} min read</span>}
        {summary && <span className="line-clamp-1 italic text-gray-600">{summary}</span>}
      </div>

      {blocks.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm italic">
          Type markdown on the left to see live block preview here.
        </div>
      ) : (
        <div className="space-y-6">
          {blocks.map((group, gIdx) => (
            <div key={group.id || gIdx} id={group.id} className="space-y-2">
              {group.heading && (
                <h2 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-1 mt-4">
                  {group.heading}
                </h2>
              )}
              {group.blocks?.map((item, itemIdx) => renderItem(item, itemIdx))}
            </div>
          ))}
        </div>
      )}

      {actions.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
          {actions.map((act, aIdx) => (
            <a
              key={aIdx}
              href={act.href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            >
              {act.label} &rarr;
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
